import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { OtpService } from '@app/services/authenticators/otp.service';
import { UserCertificate } from '@app/store/models/certificate.model';
import { GlobalState } from '@app/store/store';
import { Button } from '@infocert/ng-ui-kit/models';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs'; // removed 'timer' because not used

@Component({
  selector: 'app-otp-input',
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => OtpInputComponent),
    },
  ],
  standalone: false,
})
export class OtpInputComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() hide = false;
  @Input() submitOnEnter = true;
  @Input() hardware: boolean = false;
  @Input() timerSeconds = 40;
  @Input() startTimer = true;
  @Input() formControlName: string = '';
  @Input() resetGroup: string = 'all'; // Gruppo di reset: 'all', 'sms', 'triss', 'hardware'
  @Input() label: string = ''; // Label personalizzato per il campo OTP
  @Input() telephone: string = ''; // numero su cui arriva l'OTP (per messaggio SMS)
  @Input() error: boolean = false; // Mostra stato errore nel componente ng-ui-kit-otp
  @Input() errorDisclaimerText: string = ''; // Testo da mostrare in modalità errore
  @ViewChild('ngUiKitOtp') ngUiKitOtpRef: any;
  @Output() onUserInput: EventEmitter<void> = new EventEmitter<void>();

  innerCtrl = new FormControl<string>('', { nonNullable: true });
  refreshOtp = true; // usata per distruggere/ricreare il widget OTP

  // OTP configuration for ng-ui-kit-otp
  get otpLength(): number {
    return this.otpType === 'VASCO' ||
      this.otpType === 'HARDWARE' ||
      this.otpType === 'EXTERNAL'
      ? 6
      : 8;
  }

  get resendBtnConfig(): Button | undefined {
    if (this.hardware) {
      return undefined; // No resend for hardware tokens
    }
    const label = this.translateService.instant(
      'COMMON.ACCORDION_OTP.OTP_SEND_AGAIN'
    );
    return {
      label: label,
      altText: label,
      onClick: () => this.sendOtp(),
    };
  }

  otpType: string = 'SMS';
  value = '';

  touched = false;
  disabled = false;
  private initialized = false;


  resendDisabled = false;

  private otpResetSubscription?: Subscription;
  private storeSubscription?: Subscription;

  constructor(
    private otpService: OtpService,
    private translateService: TranslateService,
    private store: Store<GlobalState>,
    private cdr: ChangeDetectorRef
  ) {}

  private subscribeToCertificate(): void {
    this.storeSubscription = this.store.subscribe((store: GlobalState) => {
      const certificate = store?.certificate as UserCertificate | undefined;

      if (certificate?.otpType) {
        this.otpType = certificate.otpType;
      }

      if (!this.telephone && typeof certificate?.telephone === 'string') {
        const storedTelephone = certificate.telephone.trim();
        if (storedTelephone) {
          this.telephone = storedTelephone;
        }
      }
    });
  }

  ngOnInit(): void {
    this.otpResetSubscription = this.otpService
      .getEventSubject()
      .subscribe((resetType: any) => {
        if (
          resetType === 'all' ||
          this.resetGroup === 'all' ||
          resetType === this.resetGroup ||
          resetType === true
        ) {
          this.reset();
          console.log(
            `[OtpInput] reset triggered for group: ${this.resetGroup}, resetType: ${resetType}`
          );
        }
      });

    this.subscribeToCertificate();

    if (
      this.otpType === 'HARDWARE' ||
      this.otpType === 'VASCO' ||
      this.otpType === 'EXTERNAL'
    ) {
      this.hardware = true;
    }

    // Marca come inizializzato dopo un breve delay per evitare eventi spurii
    setTimeout(() => {
      this.initialized = true;
    }, 100);
  }

  ngOnDestroy(): void {
    this.otpResetSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
    // no RxJS timer cleanup needed (unused)
  }

  onChange = (value: string) => {
    const safeValue = value ?? '';
    const valueChanged = safeValue !== this.value;
    if (valueChanged) {
      this.onUserInput.emit();
    }
    console.log(
      'OTP value changed:',
      `"${safeValue}"`,
      'length:',
      safeValue?.length,
      'Expected length:',
      this.otpLength,
      'initialized:',
      this.initialized
    );

    // Aggiorna il controllo interno mantenendo il form di riferimento sincronizzato
    if (safeValue !== this.innerCtrl.value) {
      this.innerCtrl.setValue(safeValue, { emitEvent: false });
    }

    // Ignora eventi di inizializzazione spuri che potrebbero arrivare prima che il componente sia pronto
    if (!this.initialized && safeValue.length === 0) {
      console.log('Skipping initialization event');
      return;
    }

    this.value = safeValue;

    // Passa sempre il valore corrente al form: se incompleto scatterà il minLength
    const finalValue = safeValue;
    const isComplete = safeValue && safeValue.length === this.otpLength;
    console.log('Passing to form:', `"${finalValue}"`, 'isComplete:', isComplete);

    this.onChangeCallback(finalValue);

    // Marca come touched solo quando l'utente ha interagito
    this.onTouchedCallback();
  };

  onTouched = () => {};

  private onChangeCallback = (value: string) => {};
  private onTouchedCallback = () => {};

  setVal(event: any) {
    this.onChange(event.target.value);
    this.value = event.target.value;
  }

  onEnter(event: KeyboardEvent) {
    console.log('OtpInputComponent ~ onEnter ~ event', event);
    if (!this.submitOnEnter) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  /**
   * Resetta completamente il componente OTP
   * Ripulisce valori, validazioni e stati
   */
  reset() {
    // Reset del valore OTP e stato form interno
    this.value = '';
    this.innerCtrl.setValue('', { emitEvent: false });
    this.innerCtrl.markAsPristine();
    this.innerCtrl.markAsUntouched();
    this.innerCtrl.updateValueAndValidity({ emitEvent: false });

    // Prova a svuotare anche il child, se lo espone
    if (this.ngUiKitOtpRef?.writeValue) {
      this.ngUiKitOtpRef.writeValue('');
    }

    // stop/reset RxJS timer not needed (not used)
    this.resendDisabled = false;

    // Notifica il form padre (ControlValueAccessor)
    this.onChangeCallback('');

    // Hard reset UI: distruggi e ricrea il widget OTP
    // left as-is because this is where refreshOtp is used
    this.refreshOtp = false;
    this.cdr.detectChanges(); // forza distruzione
    this.refreshOtp = true;
    this.cdr.detectChanges(); // ricrea pulito

    // Stato touched
    this.touched = false;

    console.log('OTP component reset completed');
  }

  writeValue(val: string | null) {
    const safeValue = val ?? '';
    this.value = safeValue;
    this.innerCtrl.setValue(safeValue, { emitEvent: false });
    if (this.ngUiKitOtpRef?.writeValue) {
      this.ngUiKitOtpRef.writeValue(safeValue);
    }
  }

  registerOnChange(onChange: any) {
    this.onChangeCallback = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouchedCallback = onTouched;
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouchedCallback();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    if (disabled) {
      if (!this.innerCtrl.disabled) {
        this.innerCtrl.disable({ emitEvent: false });
      }
    } else if (this.innerCtrl.disabled) {
      this.innerCtrl.enable({ emitEvent: false });
    }
  }

  toggleHide() {
    this.hide = !this.hide;
  }

  sendOtp() {
    if (this.resendDisabled) return; // guardia anti-doppio click
    console.log('OtpInputComponent ~ sendOtp');
    this.resendDisabled = true; // re-enabled by onTimerEnd()

    this.otpService.generateCode().subscribe({
      next: () => {},
      error: error => {
        console.error(error);
        // if it fails, don't keep the user blocked
        this.resendDisabled = false;
      },
      complete: () => {},
    });
  }

  get disclaimerText(): string {
    if (this.hardware)
      return this.translateService.instant(
        'COMMON.ACCORDION_OTP.HARDWARE_MESSAGE'
      );
    const maskedNumber = this.maskTelephone(this.telephone);
    if (maskedNumber) {
      return this.translateService.instant(
        'COMMON.ACCORDION_OTP.OTP_MESSAGE',
        { number: maskedNumber }
      );
    }
    return this.translateService.instant('COMMON.ACCORDION_OTP.OTP_MESSAGE');
  }

  timerTextFn = (countdown: number) =>
    `The button will be enabled in ${countdown / 1000} seconds.`; // lasciata in caso di binding nel template

  onTimerEnd(): void {
    this.resendDisabled = false; // riattiva il bottone
    this.blurActiveElement(); // remove sticky focus to avoid lost clicks
    this.cdr.detectChanges(); // opzionale: forza il refresh immediato
    console.log('Timer end -> resend riattivato');
  }

  blurActiveElement(): void {
    try {
      (document.activeElement as HTMLElement)?.blur();
    } catch {}
  }

  private maskTelephone(value: string): string {
    if (!value) {
      return '';
    }
    const digits = value.replace(/\s+/g, '');
    if (digits.length <= 6) {
      return digits;
    }
    const prefix = digits.slice(0, 3);
    const suffix = digits.slice(-3);
    return `${prefix} ******${suffix}`;
  }
}
